import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaImage, FaSearch, FaSpinner, FaTrash, FaUpload } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { useAppTheme } from "../context/AppThemeContext";

const CATEGORIES = ["Events", "Sports", "Academics", "Trips", "Cultural", "Other"];
const STATUSES = ["Review", "Published"];

const statusTone = (status, isDarkMode) => {
  if (status === "Published")
    return isDarkMode ? "bg-emerald-900/40 text-emerald-300" : "bg-emerald-100 text-emerald-700";
  return isDarkMode ? "bg-amber-900/40 text-amber-300" : "bg-amber-100 text-amber-700";
};

const normalizeItem = (record = {}, index = 0) => ({
  id: record.id || record.galleryId || record._id || `GLR-${1000 + index + 1}`,
  title: record.title || "Untitled",
  category: record.category || "Events",
  uploadedBy: record.uploadedBy || record.uploaderName || "Admin",
  date: record.date || record.uploadedAt || (record.createdAt || "").slice(0, 10),
  status: record.status || "Review",
  imageUrl: record.imageUrl || record.url || record.src || "",
  description: record.description || "",
});

const extractItems = (responseData) => {
  const payload = responseData?.data ?? responseData ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.gallery)) return payload.gallery;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

export default function Gallery() {
  const { isDarkMode } = useAppTheme();
  const [activeTab, setActiveTab] = useState("manage");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadCategory, setUploadCategory] = useState("Events");
  const [uploadStatus, setUploadStatus] = useState("Review");
  const [uploadDescription, setUploadDescription] = useState("");
  const [previewImage, setPreviewImage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchGallery = async () => {
      setLoading(true);
      try {
        const response = await api.get(API_PATHS.admin.gallery());
        setItems(extractItems(response.data).map(normalizeItem));
      } catch {
        setItems([]);
        toast.error("Unable to load gallery.");
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const filteredItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return items.filter(
      (item) =>
        !q ||
        item.id.toLowerCase().includes(q) ||
        item.title.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q) ||
        item.uploadedBy.toLowerCase().includes(q),
    );
  }, [items, searchTerm]);

  const handleFilePick = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreviewImage(reader.result || "");
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!uploadTitle.trim() || !selectedFile) {
      toast.error("Title and image are required.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("title", uploadTitle.trim());
      formData.append("category", uploadCategory);
      formData.append("description", uploadDescription.trim());
      formData.append("status", uploadStatus);

      const response = await api.post(API_PATHS.admin.gallery(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const saved = normalizeItem(response.data?.data ?? response.data ?? {}, items.length);
      setItems((cur) => [saved, ...cur]);
      toast.success("Image uploaded successfully.");
      setUploadTitle("");
      setUploadDescription("");
      setUploadCategory("Events");
      setUploadStatus("Review");
      setPreviewImage("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setActiveTab("manage");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(API_PATHS.admin.galleryById(item.id));
      setItems((cur) => cur.filter((i) => i.id !== item.id));
      toast.success("Gallery item removed.");
    } catch {
      toast.error("Failed to delete item.");
    }
  };

  const panelClass = isDarkMode
    ? "border-slate-800 bg-slate-900 shadow-black/20"
    : "border-slate-200 bg-white shadow-slate-200/60";

  const tabClass = (key) =>
    `rounded-t-md border px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
      activeTab === key
        ? isDarkMode
          ? "border-amber-500/70 bg-amber-500/15 text-amber-200"
          : "border-amber-300 bg-amber-50 text-amber-700"
        : isDarkMode
          ? "border-transparent text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          : "border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700"
    }`;

  const inputClass = `w-full rounded-xl border px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100 ${
    isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500" : "border-slate-300 bg-white"
  }`;
  const labelClass = `block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`;

  return (
    <div>
      <div className={`flex flex-wrap items-center gap-2 border-b px-6 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-gray-100 bg-white"}`}>
        {[{ key: "manage", label: "Manage Gallery" }, { key: "wall", label: "Media Wall" }, { key: "upload", label: "Upload" }].map((tab) => (
          <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} className={tabClass(tab.key)}>{tab.label}</button>
        ))}
      </div>

      <div className={`border-x border-b p-6 shadow-lg ${panelClass}`}>

        {/* ── Manage ────────────────────────────────────────────────────────── */}
        {activeTab === "manage" && (
          <>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-slate-400" : "text-slate-400"}`}>Gallery control</p>
                <h2 className={`mt-1 text-2xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Manage images and approvals</h2>
              </div>
              <div className="relative w-full md:w-96">
                <FaSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`} />
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={`w-full rounded-2xl border py-2.5 pl-10 pr-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500" : "border-slate-300 bg-white"}`} placeholder="Search title, category, uploader" />
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><FaSpinner className="animate-spin text-2xl text-amber-500" /></div>
            ) : filteredItems.length === 0 ? (
              <div className={`rounded-2xl border py-12 text-center text-sm ${isDarkMode ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>
                No gallery items found.{" "}
                <button type="button" onClick={() => setActiveTab("upload")} className="font-semibold text-amber-600 hover:underline">Upload the first image.</button>
              </div>
            ) : (
              <div className={`overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className={isDarkMode ? "bg-slate-900 text-slate-300" : "bg-slate-100 text-slate-600"}>
                      <tr>
                        {["Preview", "Title", "Category", "Uploaded By", "Date", "Status", ""].map((h) => (
                          <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className={isDarkMode ? "text-slate-300" : "text-slate-800"}>
                      {filteredItems.map((item) => (
                        <tr key={item.id} className={`border-t ${isDarkMode ? "border-slate-800 hover:bg-slate-800/50" : "border-slate-200 hover:bg-slate-50"}`}>
                          <td className="px-4 py-3">
                            {item.imageUrl
                              ? <img src={item.imageUrl} alt={item.title} className="h-12 w-16 rounded-lg object-cover" />
                              : <div className={`flex h-12 w-16 items-center justify-center rounded-lg ${isDarkMode ? "bg-slate-800" : "bg-slate-100"}`}><FaImage className="text-slate-400" /></div>
                            }
                          </td>
                          <td className="px-4 py-3 font-semibold">{item.title}</td>
                          <td className="px-4 py-3">{item.category}</td>
                          <td className="px-4 py-3">{item.uploadedBy}</td>
                          <td className="px-4 py-3">{item.date}</td>
                          <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${statusTone(item.status, isDarkMode)}`}>{item.status}</span></td>
                          <td className="px-4 py-3">
                            <button type="button" onClick={() => handleDelete(item)} className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-100"><FaTrash /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Media Wall ───────────────────────────────────────────────────── */}
        {activeTab === "wall" && (
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-slate-400" : "text-slate-400"}`}>Media wall</p>
            <h2 className={`mt-1 text-2xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Published and review images</h2>
            {loading ? (
              <div className="flex justify-center py-12"><FaSpinner className="animate-spin text-2xl text-amber-500" /></div>
            ) : items.length === 0 ? (
              <p className={`mt-6 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No gallery images uploaded yet.</p>
            ) : (
              <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {items.map((item) => (
                  <article key={item.id} className={`overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800 bg-slate-800/40" : "border-slate-200 bg-slate-50"}`}>
                    {item.imageUrl
                      ? <img src={item.imageUrl} alt={item.title} className="h-44 w-full object-cover" />
                      : <div className={`flex h-44 items-center justify-center ${isDarkMode ? "bg-slate-800" : "bg-slate-100"}`}><FaImage className="text-3xl text-slate-400" /></div>
                    }
                    <div className="space-y-1 p-4">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className={`font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{item.title}</h3>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusTone(item.status, isDarkMode)}`}>{item.status}</span>
                      </div>
                      <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>{item.category} • {item.uploadedBy}</p>
                      {item.description && <p className={`text-xs ${isDarkMode ? "text-slate-500" : "text-slate-500"}`}>{item.description}</p>}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Upload ───────────────────────────────────────────────────────── */}
        {activeTab === "upload" && (
          <div className="grid gap-6 lg:grid-cols-2">
            <div className={`rounded-2xl border p-5 ${isDarkMode ? "border-slate-800 bg-slate-800/30" : "border-slate-200 bg-slate-50"}`}>
              <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-slate-400" : "text-slate-400"}`}>Upload queue</p>
              <h2 className={`mt-1 text-xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Add new gallery image</h2>

              <div className={`mt-3 rounded-xl border px-4 py-3 text-xs font-mono ${isDarkMode ? "border-slate-700 bg-slate-900 text-slate-400" : "border-slate-200 bg-white text-slate-500"}`}>
                <p className="mb-1 font-semibold not-italic text-amber-600">Backend expects multipart/form-data:</p>
                <pre className="whitespace-pre-wrap leading-5">{`POST /api/admin/gallery\nfile        → <image binary>  [required]\ntitle       → "Annual Day"    [required]\ncategory    → "Events"        [required]\ndescription → "..."           [optional]\nstatus      → "Review"        [optional]`}</pre>
              </div>

              <div className="mt-4 space-y-3">
                <label className={labelClass}>Title *<input type="text" value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} className={`mt-1 ${inputClass}`} placeholder="Annual day performance" /></label>
                <label className={labelClass}>Category<select value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)} className={`mt-1 ${inputClass}`}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></label>
                <label className={labelClass}>Status<select value={uploadStatus} onChange={(e) => setUploadStatus(e.target.value)} className={`mt-1 ${inputClass}`}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></label>
                <label className={labelClass}>Description (optional)<textarea value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)} rows={2} className={`mt-1 ${inputClass}`} placeholder="Brief description..." /></label>
                <label className={labelClass}>Pick image *<input ref={fileInputRef} type="file" accept="image/*" onChange={handleFilePick} className={`mt-1 ${inputClass}`} /></label>
                <button type="button" onClick={handleUpload} disabled={!uploadTitle.trim() || !selectedFile || uploading} className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60">
                  {uploading ? <FaSpinner className="animate-spin" /> : <FaUpload />}
                  {uploading ? "Uploading..." : "Upload to gallery"}
                </button>
              </div>
            </div>

            <div className={`rounded-2xl border p-5 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-slate-400" : "text-slate-400"}`}>Preview</p>
              <h2 className={`mt-1 text-xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Image preview</h2>
              <div className={`mt-4 flex h-64 items-center justify-center rounded-xl border border-dashed ${isDarkMode ? "border-slate-700 bg-slate-800/30" : "border-slate-300 bg-slate-50"}`}>
                {previewImage
                  ? <img src={previewImage} alt="Upload preview" className="h-full w-full rounded-xl object-cover" />
                  : <div className={`text-center ${isDarkMode ? "text-slate-500" : "text-slate-500"}`}><FaImage className="mx-auto mb-2 text-2xl" /><p className="text-sm">Select an image to preview</p></div>
                }
              </div>
              {uploadTitle && (
                <div className="mt-3 space-y-1 text-sm">
                  <p className={`font-semibold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>{uploadTitle}</p>
                  <p className={isDarkMode ? "text-slate-400" : "text-slate-500"}>{uploadCategory} · {uploadStatus}</p>
                  {uploadDescription && <p className={`text-xs ${isDarkMode ? "text-slate-500" : "text-slate-500"}`}>{uploadDescription}</p>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
